import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from './useAuth';

export function useCompanies() {
  const { currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load companies from Cloud Function
  const loadCompanies = async () => {
    if (!currentUser) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    console.log('[useCompanies] Loading companies for user:', currentUser.uid);
    setLoading(true);

    try {
      const sessionToken = localStorage.getItem('workstack_session_token');
      if (!sessionToken) {
        throw new Error('No session token found. Please refresh the page.');
      }

      const companiesList = httpsCallable(functions, 'companiesList');
      const result = await companiesList({ sessionToken });

      console.log('[useCompanies] Companies loaded:', result.data.companies.length);
      setCompanies(result.data.companies || []);
      setError(null);
    } catch (err) {
      console.error('[useCompanies] Error loading companies:', err);
      setError(err.message);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // Load companies on mount and when user changes
  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Create new company
  const createCompany = async (companyData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to create companies');
    }

    try {
      console.log('[useCompanies] Creating company:', companyData.name);
      const companiesCreate = httpsCallable(functions, 'companiesCreate');
      const result = await companiesCreate(companyData);

      console.log('[useCompanies] Company created:', result.data.company.id);

      // Reload companies list
      await loadCompanies();

      return result.data.company;
    } catch (err) {
      console.error('[useCompanies] Error creating company:', err);
      throw err;
    }
  };

  // Update existing company
  const updateCompany = async (companyId, updates) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update companies');
    }

    try {
      console.log('[useCompanies] Updating company:', companyId);
      const companiesUpdate = httpsCallable(functions, 'companiesUpdate');
      await companiesUpdate({ companyId, updates });

      console.log('[useCompanies] Company updated successfully');

      // Reload companies list
      await loadCompanies();
    } catch (err) {
      console.error('[useCompanies] Error updating company:', err);
      throw err;
    }
  };

  // Get single company by ID
  const getCompany = async (companyId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to get company');
    }

    try {
      console.log('[useCompanies] Getting company:', companyId);
      const companiesGet = httpsCallable(functions, 'companiesGet');
      const result = await companiesGet({ companyId });

      return result.data.company;
    } catch (err) {
      console.error('[useCompanies] Error getting company:', err);
      throw err;
    }
  };

  // Delete company
  const deleteCompany = async (companyId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to delete company');
    }

    try {
      console.log('[useCompanies] Deleting company:', companyId);
      const companiesDelete = httpsCallable(functions, 'companiesDelete');
      await companiesDelete({ companyId });

      console.log('[useCompanies] Company deleted successfully');

      // Reload companies list
      await loadCompanies();
    } catch (err) {
      console.error('[useCompanies] Error deleting company:', err);
      throw err;
    }
  };

  return {
    companies,
    loading,
    error,
    createCompany,
    updateCompany,
    deleteCompany,
    getCompany,
    refresh: loadCompanies
  };
}
